import PropTypes from 'prop-types';
import React, { useState, useEffect, Suspense } from "react";
import { useSelector } from "react-redux";
import { Spinner } from "reactstrap";
import { createSelector } from "reselect";
import { Routes, Route } from "react-router-dom";
import { layoutTypes } from "./constants/layout";
// Import Routes all
import { publicRoutes, generateAuthProtectedRoutes } from "./routes";

// Import all middleware
import Authmiddleware from "./routes/route";

// layouts Format
import VerticalLayout from "./components/VerticalLayout/";
import HorizontalLayout from "./components/HorizontalLayout/";
import NonAuthLayout from "./components/NonAuthLayout";

// Import scss
import "./assets/scss/theme.scss";
// Vendor CSS imported once here, in a fixed order, so mini-css-extract has a
// deterministic cross-chunk ordering and does not emit "Conflicting order"
// warnings (which CI=true turns into build-breaking errors).
import "flatpickr/dist/themes/material_blue.css";
import "react-toastify/dist/ReactToastify.css";
import { isAuthenticated, updateLastActivityTime } from "./helpers/auth-helper";
import SessionWarningModal from "./components/Common/SessionWarningModal";
import ImpersonationBanner from "./components/Common/ImpersonationBanner";
import { useSessionWarning } from "./hooks/useSessionWarning";
import { useAuth } from "./contexts/AuthContext";

// Fallback shown while a lazily-loaded route chunk is being fetched.
const RouteFallback = () => (
  <div
    className="d-flex justify-content-center align-items-center"
    style={{ minHeight: "60vh", width: "100%" }}
  >
    <Spinner color="primary" />
  </div>
);

const getLayout = (layoutType) => {
  let Layout = VerticalLayout;
  switch (layoutType) {
    case layoutTypes.VERTICAL:
      Layout = VerticalLayout;
      break;
    case layoutTypes.HORIZONTAL:
      Layout = HorizontalLayout;
      break;
    default:
      break;
  }
  return Layout;
};

const App = () => {
  // Initialize with the generated routes synchronously so the "/" route exists on the
  // first render (prevents a transient React Router 'No routes matched location "/"'
  // warning). The effect below still refreshes these when the role/auth state changes.
  const [authRoutes, setAuthRoutes] = useState(() => generateAuthProtectedRoutes());
  const [isAuthenticatedState, setIsAuthenticatedState] = useState(false);
  const [userRole, setUserRole] = useState("USER");
  
  // Session warning hook
  const { 
    showWarning, 
    timeRemaining, 
    handleContinueSession, 
    handleLogout 
  } = useSessionWarning();

  // Impersonation hook
  const { isImpersonating, impersonatedUser, stopImpersonation } = useAuth();

  // Debug impersonation state — disabled (left as debug console noise)
  // useEffect(() => {
  //   console.log('🎯 App.js - Impersonation state:', { isImpersonating, impersonatedUser });
  // }, [isImpersonating, impersonatedUser]);

  const selectLayoutState = (state) => state.Layout;
  const LayoutProperties = createSelector(
    selectLayoutState,
      (layout) => ({
        layoutType: layout.layoutType,
      })
  );

    const {
      layoutType
  } = useSelector(LayoutProperties);

  const Layout = getLayout(layoutType);

  // Track user activity for idle-based session timeout
  useEffect(() => {
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    let lastUpdateTime = 0;
    const UPDATE_THROTTLE = 2000; // Only update every 2 seconds to reduce overhead
    
    const trackActivity = () => {
      if (isAuthenticated()) {
        const now = Date.now();
        // Throttle updates to avoid excessive writes
        if (now - lastUpdateTime >= UPDATE_THROTTLE) {
          updateLastActivityTime();
          lastUpdateTime = now;
        }
      }
    };
    
    // Track activity on all specified events
    activityEvents.forEach(event => {
      window.addEventListener(event, trackActivity, { passive: true });
    });
    
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, trackActivity);
      });
    };
  }, []);

  // Monitor role changes in sessionStorage and update routes accordingly
  useEffect(() => {
    const updateRoutes = () => {
      const currentRole = sessionStorage.getItem("roleName") || "USER";
      const isAuth = isAuthenticated();
      
      setUserRole(currentRole);
      setIsAuthenticatedState(isAuth);
      
      // Always regenerate routes based on current role
      const routes = generateAuthProtectedRoutes();
      setAuthRoutes(routes);
    };

    // Update routes immediately on component mount
    updateRoutes();

    // Poll for role changes - this is critical for RESELLER routes
    // We need to check frequently when user just logged in
    const interval = setInterval(() => {
      const currentRole = sessionStorage.getItem("roleName") || "USER";
      const isAuth = isAuthenticated();
      
      // If role or auth status changed, regenerate routes
      if (currentRole !== userRole || isAuth !== isAuthenticatedState) {
        updateRoutes();
      }
    }, 100);

    // Continue checking after initial period with the updated role
    const timeout = setTimeout(() => {
      clearInterval(interval);
      
      // Switch to less frequent checks after 5 seconds
      const slowInterval = setInterval(() => {
        const currentRole = sessionStorage.getItem("roleName") || "USER";
        const isAuth = isAuthenticated();
        
        if (currentRole !== userRole || isAuth !== isAuthenticatedState) {
          updateRoutes();
        }
      }, 1000);

      // Keep checking for 30 more seconds
      const slowTimeout = setTimeout(() => {
        clearInterval(slowInterval);
      }, 30000);

      return () => {
        clearInterval(slowInterval);
        clearTimeout(slowTimeout);
      };
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [userRole, isAuthenticatedState]);

  return (
    <React.Fragment>
      {/* Impersonation Banner - Always visible when impersonating */}
      {isImpersonating && (
        <ImpersonationBanner
          impersonatedUser={impersonatedUser}
          onStopImpersonation={stopImpersonation}
        />
      )}
      
      <Routes>
        {publicRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={
              <NonAuthLayout>
                <Suspense fallback={<RouteFallback />}>
                  {route.component}
                </Suspense>
              </NonAuthLayout>
            }
            key={idx}
            exact={true}
          />
        ))}

        {authRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={
              <Authmiddleware>
                <Layout>
                  <Suspense fallback={<RouteFallback />}>
                    {route.component}
                  </Suspense>
                </Layout>
              </Authmiddleware>}
            key={idx}
            exact={true}
          />
        ))}
      </Routes>
      
      {/* Session Warning Modal */}
      <SessionWarningModal
        isOpen={showWarning}
        timeRemaining={timeRemaining}
        onContinue={handleContinueSession}
        onLogout={handleLogout}
      />
    </React.Fragment>
  );
};

App.propTypes = {
  layout: PropTypes.any
};

export default App;